/**
 * Storage utilities to help with localStorage management
 * and prevent performance issues with frequent saves
 */

// Map to track pending saves
const pendingSaves = new Map<string, {
  data: any,
  timeoutId: ReturnType<typeof setTimeout>
}>();

// Default throttle delay in milliseconds (e.g., wait 1 second before saving)
const DEFAULT_THROTTLE_DELAY = 1000;

/**
 * Throttled localStorage save function
 * Only saves after a delay and coalesces multiple saves to the same key
 * @param key localStorage key to save to
 * @param data Data to save (will be JSON stringified)
 * @param delay Delay in ms before actually saving
 */
export const throttledStorageSave = (
  key: string, 
  data: any, 
  delay: number = DEFAULT_THROTTLE_DELAY
): void => {
  try {
    // Cancel any pending save for this key
    if (pendingSaves.has(key)) {
      clearTimeout(pendingSaves.get(key)!.timeoutId);
    }
    
    // Schedule a new save
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(data));
        pendingSaves.delete(key);
      } catch (error) {
        console.error(`Error saving data to localStorage key "${key}":`, error);
      }
    }, delay);
    
    // Store pending save
    pendingSaves.set(key, { data, timeoutId });
  } catch (error) {
    console.error('Error in throttled storage save:', error);
  }
};

/**
 * Force immediate save of any pending data
 * Useful when a component is unmounting and we want to ensure data is saved
 * @param specificKey Optional - only flush a specific key instead of all pending saves
 */
export const flushPendingSaves = (specificKey?: string): void => {
  try {
    if (specificKey) {
      // Only flush a specific pending save
      const pendingSave = pendingSaves.get(specificKey);
      if (pendingSave) {
        clearTimeout(pendingSave.timeoutId);
        localStorage.setItem(specificKey, JSON.stringify(pendingSave.data));
        pendingSaves.delete(specificKey);
      }
    } else {
      // Flush all pending saves
      pendingSaves.forEach((pendingSave, key) => {
        clearTimeout(pendingSave.timeoutId);
        localStorage.setItem(key, JSON.stringify(pendingSave.data));
      });
      pendingSaves.clear();
    }
  } catch (error) {
    console.error('Error flushing pending saves:', error);
  }
};

/**
 * Safely read from localStorage with proper error handling and type casting
 * @param key localStorage key to read from
 * @param defaultValue Default value if key doesn't exist or there's an error
 */
export const safeStorageGet = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error reading from localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely remove an item from localStorage
 * @param key localStorage key to remove
 */
export const safeStorageRemove = (key: string): boolean => {
  try {
    // Cancel any pending save
    if (pendingSaves.has(key)) {
      clearTimeout(pendingSaves.get(key)!.timeoutId);
      pendingSaves.delete(key);
    }
    
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
    return false;
  }
}; 